import { useState } from "react";
import useFetchOrders from "api/OrderResource/useFetchOrders";
import Admin2 from "layouts/Admin2";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import ChangeStatus from "./ChangeStatus";
import { activeCurrencies, getPaymentMethodLabel } from "utils";
import useOrdersFilters from "./OrdersFilter/useOrdersFilters";
import { OrderFilterProvider } from "./OrdersFilter/OrderFilterContext";
import OrdersFilter from "./OrdersFilter";
// import Search from "./OrdersFilter/Search";
// import ClearFilters from "./OrdersFilter/ClearFilters";
// import RowsPerPage from "./OrdersFilter/RowsPerPage";
import { CustomPaginator } from "components";
import RowsPerPage from "admin/Filters/RowsPerPage";
import { formData } from "./OrdersFilter/ordersQueryParams";
import Search from "admin/Filters/Search";
import ClearFilters from "admin/Filters/ClearFilters";

const Orders = () => {
	const filters = useOrdersFilters();
	const [expandedRows, setExpandedRows] = useState(null);

	const { data: orders, isLoading: isLoadingOrders } = useFetchOrders({
		page: filters?.page,
		rows_per_page: filters?.rowsPerPage,
		from_date: filters?.fromDate,
		to_date: filters?.toDate,
		order_status: filters?.orderStatus,
		search: filters?.search,
	});

	const ordersList = orders?.data?.records;

	const totalRecords = orders?.data?.total_records || 0;

	const numberOfActiveFilters = filters?.allSearchParams
		?.filter((param) => !filters?.paginationParams?.includes(param))
		?.filter((param) => filters[param] !== "")?.length;

	const amountTemplate = (row) => {
		let amountLabel = "-";

		if (row?.currency === "USD") {
			amountLabel = `${activeCurrencies.USD.label} $${Number(row?.total_amount)?.toFixed(2)}`;
		} else {
			amountLabel = `${activeCurrencies.ZWL.label} $${Number(row?.total_amount)?.toFixed(2)}`;
		}

		return <span className="text-base font-bold">{amountLabel}</span>;
	};

	const paymentMethodTemplate = (row) => {
		if (row?.payment_method?.startsWith("Ecocash")) {
			return (
				<div className="flex flex-col">
					<span>{getPaymentMethodLabel(row?.payment_method)}</span>
					<span className="text-xs font-bold">{row?.phone}</span>
				</div>
			);
		}
		return <span>{row?.payment_method}</span>;
	};

	const customerTemplate = (row) => (
		<div className="flex flex-col">
			<span className="font-bold">
				{row?.user?.first_name} {row?.user?.last_name}
			</span>
			<span>{row?.user?.email}</span>
		</div>
	);

	const statusTemplate = (row) => (
		<Tag
			rounded
			className="font-sans text-xs"
			severity={row.status.toLowerCase() === "completed" ? "warn" : "danger"}
			value={row?.status}
		/>
	);

	const actionTemplate = (row) => (
		<ChangeStatus orderId={row.id} currentOrderStatus={row.status} orderNumber={row.order_number} />
	);

	const rowExpansionTemplate = (data) => {
		const hasBooks = data?.books?.length > 0;
		const hasSubscription = data?.subscription;

		if (!hasBooks && !hasSubscription) {
			return (
				<div className="p-4 bg-gray-50 border-t border-gray-200">
					<div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
						<h5 className="font-semibold text-sm text-akgreener mb-2">Order Details</h5>
						<div className="flex flex-col text-sm w-full">
							<div>
								<span className="text-gray-500">Order ID:</span>
								<span className="ml-2 font-medium">{data?.id || "-"}</span>
							</div>
							{data?.payment_method?.startsWith("Ecocash") && data?.client_correlator && (
								<>
									<div>
										<span className="text-gray-500">EcoCash client correlator:</span>
										<span className="ml-2 font-medium">{data?.client_correlator}</span>
									</div>
									<div>
										<span className="text-gray-500">TXN ID:</span>
										<span className="ml-2 font-medium">{data?.ecocash_reference}</span>
									</div>
								</>
							)}
						</div>
					</div>
					<div className="text-gray-500 text-center">No items found for this order</div>
				</div>
			);
		}

		return (
			<div className="p-4 bg-gray-50 border-t border-gray-200">
				<h4 className="font-bold text-base mb-3">Order Items</h4>

				<div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
					<h5 className="font-semibold text-sm text-akgreener mb-2">Order Details</h5>
					<div className="flex flex-col text-sm w-full">
						<div>
							<span className="text-gray-500">Order ID:</span>
							<span className="ml-2 font-medium">{data?.id || "-"}</span>
						</div>
						{data?.payment_method?.startsWith("Ecocash") && data?.client_correlator && (
							<>
								<div>
									<span className="text-gray-500">EcoCash client correlator:</span>
									<span className="ml-2 font-medium">{data?.client_correlator}</span>
								</div>
								<div>
									<span className="text-gray-500">TXN ID:</span>
									<span className="ml-2 font-medium">{data?.ecocash_reference}</span>
								</div>
							</>
						)}
					</div>
				</div>

				{hasSubscription && (
					<div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
						<h5 className="font-semibold text-sm text-akgreener mb-2">Subscription</h5>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
							<div>
								<span className="text-gray-500">Plan:</span>
								<span className="ml-2 font-medium">{data.subscription.name}</span>
							</div>
							<div>
								<span className="text-gray-500">Duration:</span>
								<span className="ml-2 font-medium">
									{data.subscription.duration} {data.subscription.time_unit}
								</span>
							</div>
							<div>
								<span className="text-gray-500">Cost:</span>
								<span className="ml-2 font-medium">
									${Number(data.subscription.cost)?.toFixed(2)}
								</span>
							</div>
						</div>
					</div>
				)}

				{hasBooks && (
					<div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
						<h5 className="font-semibold text-sm text-akgreener p-3 border-b border-gray-200">
							Books ({data.books.length})
						</h5>
						<DataTable
							value={data.books}
							className="font-sans text-sm"
							emptyMessage="No books in this order"
						>
							<Column field="title" header="Title" />
							<Column field="author" header="Author" />
							<Column field="quantity" header="Qty" style={{ width: "60px" }} />
							<Column
								field="format"
								header="Format"
								body={(row) => (
									<Tag
										value={row.format}
										severity={row.format === "Digital" ? "info" : "warning"}
										className="text-xs"
									/>
								)}
							/>
							<Column
								header="Cost"
								body={(row) => (
									<span className="font-medium">
										{row.currency === "ZWL" ? "ZWL" : "USD"} ${Number(row.total_cost)?.toFixed(2)}
									</span>
								)}
							/>
						</DataTable>
					</div>
				)}
			</div>
		);
	};

	return (
		<Admin2>
			<OrderFilterProvider>
				<div className="flex flex-col w-full mt-4">
					<DataTable
						loading={isLoadingOrders}
						value={ordersList}
						dataKey="id"
						expandedRows={expandedRows}
						onRowToggle={(e) => setExpandedRows(e.data)}
						rowExpansionTemplate={rowExpansionTemplate}
						header={
							<div className="space-y-4 font-sans text-sm">
								<h3 className="font-bold flex items-center justify-between gap-3 rounded-full max-w-max">
									<span>Subscription and purchase orders</span>
									<span className="block bg-gray-400 rounded-full text-white py-1 max-w-max px-2 text-center">
										{totalRecords}
									</span>
								</h3>
								<div className="flex items-center gap-2 justify-between ">
									<RowsPerPage filters={filters} formData={formData} />
									<Search
										filters={filters}
										formData={formData}
										placeholder="Search order ID, number, reference etc..."
									/>
									<ClearFilters filters={filters} numberOfActiveFilters={numberOfActiveFilters} />
									<OrdersFilter numberOfActiveFilters={numberOfActiveFilters} />
								</div>
							</div>
						}
						tableClassName="font-sans text-sm w-full"
						autoLayout
					>
						<Column expander style={{ width: "3rem" }} />
						<Column header="Customer" body={customerTemplate} />
						<Column field="order_number" header="Order Number" />
						<Column
							body={(row) => (
								<span className="text-akgreener">
									{row?.ecocash_reference ? row?.ecocash_reference : row?.reference}
								</span>
							)}
							header="Reference"
						/>
						<Column header="Payment method" body={paymentMethodTemplate} />
						<Column field="payment_type" header="Model" />
						<Column header="Amount" body={amountTemplate} />
						<Column field="created_at" header="Date created" />
						<Column header="Status" body={statusTemplate} />
						<Column header="Action" body={actionTemplate} />
					</DataTable>

					<div className="flex-1 flex justify-center mt-6">
						<CustomPaginator
							firstPage={filters?.firstPage}
							setFirstPage={(val) => filters?.changeFilter("firstPage", val)}
							rowsPerPage={filters?.rowsPerPage}
							setRowsPerPage={(val) => filters?.changeFilter("rowsPerPage", val)}
							totalRecords={totalRecords}
							setPage={(val) => filters?.changeFilter("page", val)}
							showRowsPerPageDropdown={false}
						/>
					</div>
				</div>
			</OrderFilterProvider>
		</Admin2>
	);
};

export default Orders;
